<script lang="ts">
	import type { ConfiguratorState } from '$lib/state/configurator.svelte';
	import type { ShopItemCarouselBlock } from '$models/Block/ShopItemCarouselBlock';
	import type { OrderConfigurator } from '$models/OrderConfigurator/OrderConfigurator';
	import type { Product } from '$models/Product/Product';
	import type { ShopCategory } from '$models/Shop/ShopCategory';
	import type { ShopItem } from '$models/Shop/ShopItem';
	import SliderWrapper from '$components/UI/Slider/SliderWrapper.svelte';
	import { trackAddToCart, trackEvent } from '$lib/datalayer/datalayer';
	import { getProductIntensityDots } from '$lib/product/intensity';
	import { getShopPresentation } from '$lib/shop/presentation.svelte';
	import { getCartState } from '$lib/state/cart.svelte';
	import {
		createConfiguratorState,
		getConfiguratorShopItemPrice,
		getShopPricing
	} from '$lib/state/configurator.svelte';
	import { getToastStore } from '$lib/stores';
	import { formatCurrency } from '$lib/utils';
	import { untrack } from 'svelte';

	// Component properties
	const { block }: { block: ShopItemCarouselBlock } = $props();

	const cartState = getCartState();
	const shopPricing = getShopPricing();
	const presentation = getShopPresentation();
	const toasts = getToastStore();

	const configurators = new Map<string, ConfiguratorState>();
	let shopCategories = $derived(
		block.shopItems
			.map((item) => item.categories)
			.flat()
			.filter((e, i, self) => i === self.findIndex((f) => e.slug === f.slug))
	);
	let selectedShopCategory = $state(
		untrack(() => (shopCategories.length > 0 ? shopCategories[0] : null))
	);
	let filteredShopItems = $derived(
		block.shopItems.filter(
			(item) =>
				!selectedShopCategory || item.categories.find((c) => c.slug === selectedShopCategory?.slug)
		)
	);

	function getProductFromConfigurator(configurator: OrderConfigurator): Product {
		return configurator.configuration.categories[0].products[0].product;
	}

	function toggleShopCategory(category: ShopCategory) {
		if (!selectedShopCategory) {
			return (selectedShopCategory = category);
		}

		if (selectedShopCategory && selectedShopCategory.slug !== category.slug) {
			return (selectedShopCategory = category);
		}

		if (selectedShopCategory && selectedShopCategory.slug === category.slug) {
			return (selectedShopCategory = null);
		}
	}

	function getItemConfiguratorState(item: ShopItem) {
		if (!item.configurator) {
			return null;
		}

		if (configurators.has(item.slug)) {
			return configurators.get(item.slug);
		}

		const configurator = createConfiguratorState(item.configurator.configuration, {
			pricingSource: shopPricing ? 'total-price' : 'order-price'
		});
		if (shopPricing && item.couponCode)
			configurator.setCouponCodes({ recurringCode: item.couponCode, onetimeCode: item.couponCode });

		configurators.set(item.slug, configurator);

		return configurator;
	}

	function handleSubmit(configurator: ConfiguratorState) {
		if (!configurator.canSubmitPricing()) return;
		const order = configurator.order();
		const result = cartState.addOrder(order);

		if (result.mixedCartBlocked) {
			toasts.trigger({
				type: 'warning',
				duration: 6000,
				message:
					'Je winkelmand is aangepast: bonen en cups kunnen nog niet samen in één bestelling. We werken hard aan een update zodat dit binnenkort wél kan.'
			});

			trackEvent('multi_cart_blocked');
		}

		if (configurator.isValid() && order) {
			trackAddToCart(order);
		}

		cartState.open();
	}
</script>

<div class="shop-item-carousel">
	{#if block.title || block.subtitle}
		<div class="shop-item-carousel-header">
			{#if block.title}
				<h2 class="shop-item-carousel-title">{block.title}</h2>
			{/if}
			{#if block.subtitle}
				<p class="shop-item-carousel-text">{block.subtitle}</p>
			{/if}
		</div>
	{/if}

	{#if shopCategories.length > 1}
		<div class="shop-item-carousel-filters">
			{#each shopCategories as category, i (category)}
				<button
					class={[
						'shop-item-carousel-filters-button',
						selectedShopCategory && selectedShopCategory.slug === category.slug && 'is-active'
					]}
					onclick={() => toggleShopCategory(category)}>{category.name}</button
				>
			{/each}
		</div>
	{/if}

	<div class="shop-item-carousel-slider">
		<SliderWrapper options={{ loop: false, align: 'start', inViewThreshold: 1 }}>
			{#snippet slides(Slide)}
				{#each filteredShopItems as item, i}
					{@const product = item.configurator
						? getProductFromConfigurator(item.configurator)
						: null}
					{@const configuratorState = getItemConfiguratorState(item)}
					{@const itemPrice =
						configuratorState && presentation.ready
							? getConfiguratorShopItemPrice(configuratorState, presentation.vat)
							: null}
					{@const packageUnitName =
						configuratorState?.selectedCategory()?.translations.packageUnitName ?? 'doos'}
					{@const isInCart = product && cartState.hasProduct(product.id)}
					<Slide>
						<div class="shop-item-carousel-item">
							<div class="shop-item-carousel-item-content">
								<div
									class={[
										'shop-item-carousel-item-info',
										item.card.textColor === 'dark' && 'is-dark',
										item.card.textColor === 'light' && 'is-light'
									]}
								>
									{#if item.title}
										<h3 class="shop-item-carousel-item-title">
											{item.title}
										</h3>
									{/if}

									{#if item.card.description}
										<p class="shop-item-carousel-item-text">
											{item.card.description}
										</p>
									{/if}

									{#if product?.intensity}
										<div class="shop-item-carousel-item-intensity">
											<div class="shop-item-carousel-item-intensity-label">Intensiteit</div>
											<div class="shop-item-carousel-item-intensity-dots">
												{#each getProductIntensityDots(product.intensity) as dot}
													<div
														class={[
															'shop-item-carousel-item-intensity-dot',
															dot.isActive && 'is-filled',
															dot.isHalf && 'is-half'
														]}
													></div>
												{/each}
											</div>
											<div class="shop-item-carousel-item-intensity-value">
												{product.intensity}/12
											</div>
										</div>
									{/if}
								</div>
								<div class="shop-item-carousel-item-footer">
									<div class="shop-item-carousel-item-prices">
										{#if itemPrice !== null}
											<div class="shop-item-carousel-item-price">
												{formatCurrency(itemPrice)} /
												<small>per {packageUnitName}</small>
											</div>
											{#if product?.quantityMultiplier && product.quantityMultiplier > 1}
												<div class="shop-item-carousel-item-price-per-cup">
													{formatCurrency(itemPrice / product.quantityMultiplier)}
													per {configuratorState?.selectedCategory()?.translations.unitNameSingle}
												</div>
											{/if}
										{/if}
									</div>
									<button
										class={['shop-item-carousel-item-button', isInCart && 'is-added']}
										disabled={isInCart ||
											!configuratorState ||
											!configuratorState.canSubmitPricing()}
										type="button"
										onclick={() => configuratorState && handleSubmit(configuratorState)}
									>
										<span class="screenreader">Voeg toe</span>
									</button>
								</div>
							</div>
							{#if item.card.image}
								<figure class="shop-item-carousel-item-image">
									<img
										src={item.card.image.url}
										width={item.card.image.width}
										height={item.card.image.height}
										alt={item.card.image.alt || ''}
										loading="lazy"
										decoding="async"
									/>
								</figure>
							{/if}
						</div>
					</Slide>
				{/each}
			{/snippet}
		</SliderWrapper>
	</div>
</div>

<style lang="postcss" global>
	.shop-item-carousel {
		&-header {
			&:not(:last-child) {
				margin-bottom: var(--spacing-6);
			}
		}

		&-title {
			font-size: var(--font-size-5);

			&:not(:last-child) {
				margin-bottom: var(--spacing-2);
			}

			@media (--sm) {
				font-size: var(--font-size-6);
			}
		}

		&-filters {
			display: flex;
			gap: var(--spacing-2);

			&:not(:last-child) {
				margin-bottom: var(--spacing-6);
			}

			&-button {
				display: block;
				padding: 10px 14px;
				border: 1px solid var(--color-brown);
				border-radius: var(--radius-2);
				background-color: transparent;
				color: var(--color-brown);
				font-size: var(--font-size-0);
				font-weight: var(--base-font-weight);
				line-height: var(--font-lineheight-1);
				border: 1px solid var(--color-brown);
				border-radius: 21px;
				transition:
					color var(--transition),
					background-color var(--transition),
					border-color var(--transition);

				@media (--small) {
					font-size: var(--font-size-1);
					padding: 10px var(--spacing-5);
				}

				&.is-active {
					color: var(--color-white);
					background-color: var(--color-brown);
				}

				&:hover {
					color: var(--color-white);
					background-color: var(--color-brown);
				}
			}
		}

		&-slider {
			position: relative;
		}

		.slider,
		.slider-viewport {
			overflow: visible;
		}

		.slider {
			--slide-size: 318px;

			@media (--extra-large) {
				--slide-size: 25%;
			}
		}

		&-item {
			& {
				flex: 1;
				display: block;
				position: relative;
				border-radius: var(--radius-5);
				overflow: hidden;
			}
			&-image {
				img {
					display: block;
					width: 100%;
					height: auto;
				}
			}

			&-content {
				position: absolute;
				top: var(--spacing-6);
				left: var(--spacing-6);
				right: var(--spacing-6);
				bottom: var(--spacing-6);
				display: flex;
				flex-direction: column;
			}

			&-info {
				padding: var(--spacing-4);
				border-radius: var(--radius-3);
				background-color: rgba(255, 255, 255, 0.3);
			}

			&-title {
				font-size: var(--font-size-4);
				line-height: var(--font-lineheight-0);
				margin-bottom: var(--spacing-1);

				.is-light & {
					color: var(--color-white);
				}
			}

			&-text {
				font-size: var(--font-size-0);

				.is-light & {
					color: var(--color-white);
				}
			}

			&-intensity {
				position: relative;
				margin-top: var(--spacing-2);
				padding-top: var(--spacing-3);
				font-size: 11px;
				line-height: var(--font-lineheight-0);
				display: flex;
				align-items: center;
				gap: var(--spacing-1);

				&::before {
					content: '';
					position: absolute;
					left: 0;
					right: 0;
					top: 0;
					height: 2px;
					background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
					background-size: 5px 2px;
					background-repeat: repeat-x;
					pointer-events: none;
				}

				.is-light & {
					color: var(--color-white);

					&::before {
						background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23ffffff'/%3E%3C/svg%3E");
					}
				}

				&-dots {
					display: flex;
					align-items: center;
					gap: 4px;
				}

				&-dot {
					display: block;
					width: 10px;
					height: 10px;
					border-radius: 50%;
					border: 1px solid var(--color-brown);
					background-color: transparent;

					.is-light & {
						border-color: var(--color-white);
					}

					&.is-half {
						background: linear-gradient(
							to right,
							var(--color-brown) 0%,
							var(--color-brown) 60%,
							transparent 60%,
							transparent 100%
						);

						.is-light & {
							background: linear-gradient(
								to right,
								var(--color-white) 0%,
								var(--color-white) 60%,
								transparent 60%,
								transparent 100%
							);
						}
					}

					&.is-filled {
						background-color: var(--color-brown);

						.is-light & {
							background-color: var(--color-white);
						}
					}
				}
			}

			&-footer {
				display: flex;
				gap: var(--spacing-2);
				margin-top: auto;
			}

			&-prices {
				display: flex;
				flex-direction: column;
				gap: var(--spacing-1);
				line-height: var(--font-lineheight-0);
			}

			&-price {
				font-size: var(--font-size-2);
				font-family: var(--font-secondary);
				font-weight: normal;
				text-transform: uppercase;
			}

			&-price-per-cup {
				font-size: var(--font-size-0);
			}

			&-button {
				margin-top: auto;
				margin-left: auto;
				background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxMiAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTYuOSA2LjlWMTJINS4xVjYuOUgwTDAgNS4xSDUuMUw1LjEgMEg2LjlMNi45IDUuMUwxMiA1LjFWNi45TDYuOSA2LjlaIiBmaWxsPSIjNTkyRTJDIi8+Cjwvc3ZnPg==');
				background-size: 12px 12px;
				background-repeat: no-repeat;
				background-position: center;
				background-color: var(--color-white);
				width: 48px;
				height: 48px;
				display: flex;
				align-items: center;
				justify-content: center;
				border-radius: var(--radius-4);

				&.is-added {
					opacity: 0.3;
					background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUiIGhlaWdodD0iMTEiIHZpZXdCb3g9IjAgMCAxNSAxMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTQuODUgMTAuNjVMMCA1LjgyNUwxLjI3NSA0LjUyNUw0Ljg1IDguMUwxMi45NzUgMEwxNC4yNSAxLjI3NUw0Ljg1IDEwLjY1WiIgZmlsbD0iIzU5MkUyQyIvPgo8L3N2Zz4K');
					background-size: 15px 11px;
					pointer-events: none;
				}
			}
		}
	}
</style>
